import React, { useCallback, useEffect, useRef, useState } from "react";
import "./SportRouteDetail.scss";
import "../SportRoutes/Components/RouteYouEmbed/RouteYouStyles.scss";
import { Carousel } from "../Carousel/Carousel";
import SportRouteRating from "./components/SportRouteRating";
import CommentCount from "./components/CommentCount";
import SportRouteComments from "./components/SportRouteComments";
import SportRouteStar from "./components/SportRouteStar";
import DifficultyPill from "./components/DifficultyPill/DifficultyPill";
import Types from "../../constants/routeTypes";

import {
  getNodeText,
  hasSportRouteCommentsLoaded,
  parseDate,
  scoreToOutOfFive,
} from "./utils/utils";
import SportRouteTooltip from "./components/SportRouteTooltip";
import { Button } from "../Button/Button";
import { debounce } from "lodash";
import useClickOutside from "./hooks/useClickOutside";
import SportRouteNoComments from "./components/SportRouteNoComments";
import FriendlyCaptcha from "./components/FriendlyCaptcha";

export default function SportRouteDetail(props) {
  const { routeData, dictionary, feedbackGuidelines, meldpuntUrl, captcha } =
    props;
  const isOffroadBikeRoute = Types.OffroadBikeRoutes.includes(routeData.type.id);
  const routeViewerMapClassName = `c-routedetail-map${
    isOffroadBikeRoute ? " c-routedetail-map--hidden" : ""
  }`;

  const [routeDetails, setRouteDetails] = useState();
  const [showAllImages, setShowAllImages] = useState(false);
  const [startPlaces, setStartPlaces] = useState([]);
  const [title, setTitle] = useState("");
  const [loop, setLoop] = useState("");
  const [media, setMedia] = useState([]);
  const [url, setUrl] = useState("");
  const [thumbnail, setThumbnail] = useState([
    "maxresdefault",
    "mqdefault",
    "sddefault",
    "hqdefault",
    "default",
  ]);
  const [clickedImageIndex, setClickedImageIndex] = useState(0);
  const commentsArea = useRef(null);
  const commentsProcessed = useRef(false);
  const tabPlane = useRef(null);
  const tabPlaneProcessed = useRef(false);
  const printContainer = useRef(null);
  const downloadContainer = useRef(null);
  const [routeScore, setRouteScore] = useState();
  const [tabPlaneObserverRunning, setTabPlaneObserverRunning] = useState(false);
  const [commentAreaObserverRunning, setCommentAreaObserverRunning] =
    useState(false);
  const [isPrintOpen, setIsPrintOpen] = useState(false);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const [description, setDescription] = useState("");
  const isCaptchaOk = useRef(false);
  const submitButton = useRef(null);

  useEffect(() => {
    setRouteDetails(routeData);
    getStartPlaces(routeData);
    splitTitle(routeData);
    getMedia(routeData);
    setDescription(removeEmptyParagraphsAfterLastUl(routeData));
  }, []);

  useEffect(() => {
    const cleanup = startTabPlaneObserver();
    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [routeDetails]);

  useEffect(() => {
    const cleanup = startCommentAreaMutationObserver();
    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [commentsArea]);

  const getStartPlaces = (data) => {
    let startplaces = [];
    data.pois.forEach((poi) => {
      if (poi.location?.type && poi.location.type.id == 123) {
        startplaces.push(poi.location);
      }
    });
    setStartPlaces(startplaces);
  };

  const getMedia = (data) => {
    let media = [];
    data.media.forEach((el) => {
      if (el.source?.name?.nl && el.source.name.nl === "YouTube") {
        //Yt vid
        media.push(prepYtVideo(el));
      } else {
        //Foto
        if (el?.uri?.nl) {
          media.push(prepImage(el));
        }
      }
    });
    setMedia(media);
  };

  const prepYtVideo = (media) => {
    if (media.content?.nl) {
      setUrl(
        "https://img.youtube.com/vi/" + media.content.nl + "/maxresdefault.jpg"
      );
    } else {
      setUrl(null);
    }
    return {
      type: "youtube",
      url: media.uri?.nl ? media.uri.nl : null,
    };
  };

  const prepImage = (media) => {
    return {
      type: "image",
      image: media.uri?.nl ? changeDimensions(media.uri.nl) : null,
    };
  };

  const changeDimensions = (url) => {
    const newDimensions = "1200x900";
    const modifiedUrl = url.replace(/\/\d+x\d+\//, `/${newDimensions}/`);

    return modifiedUrl;
  };

  const convertTypeToIcon = (type) => {
    let image = "";
    let baseUrl = "/images/listviewIcons";
    let suffix = ".svg";
    switch (type) {
      case 9:
      case 25:
      case 26:
      case 49:
        image = `${baseUrl}/runIcon${suffix}`;
        break;

      case 17:
      case 35:
      case 36:
        image = `${baseUrl}/horsebackIcon${suffix}`;
        break;

      case 7:
        image = `${baseUrl}/mountainbikeIcon${suffix}`;
        break;

      case 47:
        image = `${baseUrl}/gravelbikeIcon${suffix}`;
        break;

      case 16:
      case 32:
        image = `${baseUrl}/kayakIcon${suffix}`;
        break;

      case 19:
        image = `${baseUrl}/skateIcon${suffix}`;
        break;

      case 2:
      case 4:
      case 8:
        image = `${baseUrl}/multiMoveIcon${suffix}`;
        break;
    }
    return image;
  };

  const splitTitle = (data) => {
    var title = "";
    if (data.name.nl) {
      title = data.name.nl;

      const delimiter = title.includes("•")
        ? "•"
        : title.includes("-")
        ? "-"
        : null;

      if (delimiter) {
        const split = title.split(delimiter);
        const last = split[split.length - 1].toLowerCase();
        const colors = ["blauw", "rode", "groen", "zwart"];

        const containsColor = colors.some((color) => last.includes(color));

        if (containsColor) {
          setTitle(split.slice(0, -1).join(delimiter).trim());
          setLoop(split[split.length - 1].trim());
        } else {
          setTitle(title);
          setLoop("");
        }
      } else {
        setTitle(title);
        setLoop("");
      }
    }
  };

  const checkRouteTypeHoogteMeters = () => {
    var show = true;
    if (routeDetails?.type?.id) {
      switch (routeDetails.type.id) {
        case 16:
        case 32:
          show = false;
          break;
        case 19:
          show = false;
          break;
        case 2:
        case 4:
        case 8:
          show = false;
          break;
      }
    }
    return show;
  };

  const navigateBack = () => {
    const url = window.location.href;
    const [beforeRoute, queryString, ...rest] = url.split(
      `route/${routeData.id}/`
    );

    if (queryString) {
      window.location.href = beforeRoute + queryString;
      return;
    }

    window.location.href = beforeRoute;
  };

  const closeOverlay = () => {
    var overlay = document.querySelector(".overlay");
    var carousel = document.querySelector(".c-routedetail-carousel__container");
    if (overlay && carousel) {
      document.body.classList.remove("u-noscroll");
      overlay.style.display = "none";
      carousel.style.display = "none";
    }
  };

  const openOverlay = (index) => {
    setClickedImageIndex(index);
    var overlay = document.querySelector(".overlay");
    var carousel = document.querySelector(".c-routedetail-carousel__container");
    if (overlay && carousel) {
      document.body.classList.add("u-noscroll");
      overlay.style.display = "block";
      carousel.style.display = "flex";
    }
  };

  const checkThumbnail = (e) => {
    var imageUrl = e.target.src;

    if (imageUrl === null) {
      return;
    }

    if (e.target.naturalWidth === 120 && e.target.naturalHeight === 90) {
      for (let i = 0; i < thumbnail.length - 1; i++) {
        if (imageUrl.indexOf(thumbnail[i]) > 0) {
          setUrl(imageUrl.replace(thumbnail[i], thumbnail[i + 1]));
          break;
        }
      }
    }
  };

  const extractCoordinates = (coordinates) => {
    // coordinates format: {wkt: 'POINT(4.901845 51.21313)}
    const coords = coordinates.match(/\((.*)\)/)?.pop();
    const coordsArray = coords.split(" ");

    return `${coordsArray[1]} ${coordsArray[0]}`;
  };

  const startCommentAreaMutationObserver = () => {
    let hasComments = false;
    if (!commentsArea.current || commentAreaObserverRunning) {
      return;
    }

    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          if (commentsProcessed.current && hasComments) {
            observer.disconnect();
            return;
          }

          if (mutation.addedNodes[0].nodeType === Node.ELEMENT_NODE) {
            let hasReviews = false;
            // check if node has a descendant with class routeYou_commentStatsBar
            if (
              mutation.addedNodes[0].querySelector(".routeYou_commentStatsBar")
            ) {
              hasReviews = renderNewStats();
              hasComments = true;
            }
            debounceObserver(hasReviews);
          }
        }
      }
    });

    observer.observe(commentsArea.current, {
      childList: true, // Observe additions/removals of child nodes
      subtree: true, // Only watch the target element's *direct* children.  Set to true if needed.
    });

    setCommentAreaObserverRunning(true);

    return () => {
      observer.disconnect();
    };
  };

  function addCaptcha() {
    const button = commentsArea.current.querySelector(
      ".routeYou_commentArea > .largeButton"
    );
    const parent = button.parentNode;
    submitButton.current = button;
    const HTMLCaptcha = document.createElement("div");
    button.parentNode.insertBefore(HTMLCaptcha, button);
    button.remove();

    ReactDOM.render(
      <FriendlyCaptcha siteKey={captcha} cb={captchaCb} />,
      HTMLCaptcha
    );
    const newButton = document.createElement("button");
    newButton.classList.add("js-routeYouCommentSubmitTemp");
    newButton.classList.add("largeButton");
    newButton.textContent = dictionary.submitReviewButtonText;
    newButton.addEventListener("click", () => {
      if (!isCaptchaOk.current) {
        error.textContent = dictionary.captchaRequired;
        error.style.display = "block";
        return;
      }

      error.style.display = "none";
      submitButton.current.click();
    });

    submitButton.current.addEventListener("click", () => {
      if (isCaptchaOk.current) {
        error.style.display = "none";
      }
    });

    const error = document.createElement("div");
    error.textContent = dictionary.captchaFailed;
    error.style.display = "none";
    error.classList.add("c-routedetail__comments__error");
    parent.appendChild(error);
    parent.appendChild(newButton);

    function captchaCb(success) {
      if (success) {
        isCaptchaOk.current = true;
        const parent = commentsArea.current.querySelector(
          ".routeYou_commentArea"
        );
        parent.appendChild(submitButton.current);
        parent.querySelector(".js-routeYouCommentSubmitTemp").remove();
        error.style.display = "none";
      } else {
        isCaptchaOk.current = false;
        error.style.display = "block";
        error.innerText = dictionary.captchaFailed;
      }
    }
  }

  const debounceObserver = debounce(function (showCommentCount) {
    if (commentsProcessed.current) {
      return;
    }
    if (!hasSportRouteCommentsLoaded()) {
      return;
    }

    replaceCommentsTitle();
    hideNoCommentsFoundParagraph();
    addFeedbackGuideLines();
    addToolTips();
    replaceStars();
    const count = replaceComments();
    if (showCommentCount) {
      addCount(count);
    }
    addCaptcha();
    commentsProcessed.current = true;
  }, 200);

  const replaceCommentsTitle = () => {
    if (commentsArea.current) {
      const commentStats = commentsArea.current.querySelector(
        ".routeYou_commentStats"
      );
      if (commentStats) {
        const titleElement = commentStats.querySelector("h3");
        if (titleElement) {
          titleElement.innerText = dictionary.reviewsTitle;
        }
      }
    }
  };

  const replaceStars = () => {
    document
      .querySelectorAll(".routeYou_commentFeedbackFormIcon svg")
      .forEach((icon) => {
        icon.innerHTML = "";
        ReactDOM.render(<SportRouteStar filled={false} />, icon.parentNode);
      });
  };

  const addFeedbackGuideLines = () => {
    const guidelines = feedbackGuidelines;
    const guidelinesDiv = document.createElement("div");
    guidelinesDiv.innerHTML = guidelines;
    guidelinesDiv.classList.add("c-sportroute__feedback-guidelines");

    document.querySelector(".routeYou_commentArea h3").after(guidelinesDiv);
  };

  const addToolTips = () => {
    const parentDivs = commentsArea.current.querySelectorAll(
      ".routeYou_commentsFormRowRange"
    );
    parentDivs.forEach((parentDiv) => {
      const tooltip = Array.from(parentDiv.children).find(
        (child) => child.classList.length === 0 && child.tagName === "DIV"
      );
      if (!tooltip) {
        return;
      }
      const tooltipBody = tooltip.innerText;
      tooltip.remove();

      const toolTipDiv = document.createElement("div");
      const span = document.createElement("span");
      const label = parentDiv.querySelector("label");

      toolTipDiv.classList.add("c-sportroute__toolTip");
      label.classList.add("c-sportroute__comment-form__label");

      span.innerText = label.innerText;
      label.innerText = "";

      label.appendChild(span);
      label.appendChild(toolTipDiv);

      const id = Math.random().toString(36).substring(7);

      ReactDOM.render(
        <SportRouteTooltip id={id} content={tooltipBody} />,
        toolTipDiv
      );
    });
  };

  const renderNewStats = () => {
    if (!commentsArea.current) return;

    const statItems = commentsArea.current.querySelectorAll(
      ".routeYou_commentStats .routeYou_commentStatsItem"
    );
    let hasReviews = false;

    statItems.forEach((item, index) => {
      const bar = item.querySelector(".routeYou_commentStatsBar");
      if (!bar || !bar.firstChild || !bar.firstChild.style?.width) return;

      const score = scoreToOutOfFive(bar.firstChild.style.width);

      const span = item.querySelector("p > span");
      const count = span?.innerText.replace("(", "").replace(")", "") ?? "0";
      span?.remove();

      const title = item.querySelector("p")?.innerText.replace("*", "") ?? "";

      // Clear existing content
      item.innerHTML = "";

      if (index === 0 && count !== "0") {
        setRouteScore(score);
      }

      if (count === "0") {
        item.remove();
        return;
      }

      hasReviews = true;
      ReactDOM.render(
        <SportRouteRating score={score} count={count} title={title} />,
        item
      );
    });

    if (!hasReviews) {
      const parent = commentsArea.current.querySelector(
        ".routeYou_commentStats"
      );
      if (parent) {
        const div = document.createElement("div");
        parent.appendChild(div);
        ReactDOM.render(
          <SportRouteNoComments text={dictionary.geenComments} />,
          div
        );
      }
    }
    debounceObserver(hasReviews);
  };

  const addCount = (commentCount) => {
    // add child to routeYou_commentStats to render the comment count
    if (commentCount === 0) {
      return;
    }

    const commentStats = commentsArea.current.querySelector(
      ".routeYou_commentStats"
    );
    const div = document.createElement("div");
    commentStats.appendChild(div);

    ReactDOM.render(<CommentCount count={commentCount} />, div);
  };

  const replaceComments = () => {
    if (!commentsArea.current) {
      return;
    }

        let commentsData = [];
        const target = 'sport vlaanderen gebruiker';

        const comments = [...commentsArea.current.querySelectorAll('.routeYou_comment')]
            .filter(c => c.querySelector('.routeYou_commentOwnerName')?.textContent
                    .trim().toLowerCase() === target);
        if (comments.length === 0) {
            return
        }        
        let feedbackCommentCount = 0;
        comments.forEach((comment) => {
            const feedbackDiv = comment.querySelector('.routeYou_commentFeedback');
            if (feedbackDiv?.innerHTML) {
                feedbackCommentCount++;
            }

            feedbackDiv.querySelectorAll('.routeYou_commentFeedbackValueIcon').forEach(icon => {
                // replace with <SportRouteStar>
                icon.innerHTML = "";
                ReactDOM.render(<SportRouteStar filled={true}/>, icon)
            })
            const generalFeedback = feedbackDiv.querySelector('.routeYou_commentFeedbackValue')
            let numberText = '';
            if (generalFeedback) {
                numberText = getNodeText(generalFeedback);
            }
            const tagsResult = getTags(feedbackDiv);
            const username = tagsResult.username ?? comment.querySelector('.routeYou_commentOwnerName').innerText;
            const tags = tagsResult.tags;

            const commentData = {
                username: username,
                date: parseDate(comment.querySelector('.routeYou_commentDate').innerText),
                feedback: feedbackDiv.innerHTML,
                text: comment.querySelector('.routeYou_commentText').innerHTML,
                generalFeedback: numberText,
                ratingStars: getFeedbackRange(feedbackDiv),
                tags: tags
            }

      commentsData.push(commentData);
    });

    const parent = comments[0].parentNode;
    parent.innerHTML = "";

    ReactDOM.render(<SportRouteComments comments={commentsData} />, parent);

    return feedbackCommentCount;
  };

  function hideNoCommentsFoundParagraph() {
    if (!commentsArea.current) return;
    const pTags = commentsArea.current.querySelectorAll('p');
    pTags.forEach(p => {
      if (p.textContent.trim() === 'Geen reacties gevonden.') {
        p.style.display = 'none';
      }
    });
  }

  const getFeedbackRange = (feedbackDiv) => {
    const feedbackRange = feedbackDiv.querySelector(
      ".routeYou_commentFeedbackRange"
    );
    const feedbackRangeResult = [];
    if (!feedbackRange) {
      return feedbackRangeResult;
    }
    const rows = feedbackRange.querySelectorAll(".routeYou_commentFeedbackRow");

    rows.forEach((row) => {
      const title = row.querySelector(
        ".routeYou_commentFeedbackLabel"
      ).innerText;
      const score = getNodeText(
        row.querySelector(".routeYou_commentFeedbackValue")
      );
      feedbackRangeResult.push({ title: title, score: score });
    });
    return feedbackRangeResult;
  };

    const getTags = (feedbackDiv) => {
        const tagArea = feedbackDiv.querySelector('.routeYou_commentFeedbackInput');
        const tags = [];
        let extractedUsername = null;
        const NAME_FIELD = 'NAAM';

        if (!tagArea) {
            return { tags, username: extractedUsername };
        }

    const rows = tagArea.querySelectorAll(".routeYou_commentFeedbackRow");
    rows.forEach((row) => {
      const labelSpan = row.querySelector(".routeYou_commentFeedbackLabel");
      const valueDiv = labelSpan.nextElementSibling;

            if (labelSpan && valueDiv && valueDiv.classList.contains('routeYou_commentFeedbackValue')) {
                const title = labelSpan.textContent.trim();
                const text = valueDiv.textContent.trim();
                if (title === NAME_FIELD && text !== "/") {
                    extractedUsername = text;
                    return;
                }

                tags.push({title, text});
            }
        });
        return { tags, username: extractedUsername };
    }


  const scrollToComments = (e) => {
    e.preventDefault();
    commentsArea.current.scrollIntoView({ behavior: "smooth" });
  };

  const startTabPlaneObserver = () => {
    if (!tabPlane.current || tabPlaneObserverRunning) {
      return;
    }

    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          if (tabPlaneProcessed.current) {
            observer.disconnect();
            return;
          }

          if (mutation.addedNodes[0].nodeType === Node.ELEMENT_NODE) {
            debounceTabPlaneObserver();
          }
        }
      }
    });

    observer.observe(tabPlane.current, {
      childList: true, // Observe additions/removals of child nodes
      subtree: true, // Only watch the target element's *direct* children.  Set to true if needed.
    });
    setTabPlaneObserverRunning(true);

    // Cleanup function to disconnect the observer when the component unmounts
    return () => {
      observer.disconnect();
    };
  };

  const debounceTabPlaneObserver = debounce(function () {
    if (tabPlaneProcessed.current) {
      return;
    }
    if (!tabPlaneLoaded()) {
      return;
    }

    renderDownloadSection();
    tabPlaneProcessed.current = true;
  }, 200);

  const tabPlaneLoaded = () => {
    return tabPlane.current.querySelector(".routeYou_download");
  };

  const renderDownloadSection = () => {
    const downloadSection =
      tabPlane.current.querySelector(".routeYou_download");

    const titles = downloadSection.querySelectorAll("h3");
    titles.forEach((title) => {
      if (title.innerText === "Afdrukken") {
        const printDropdown = title.nextElementSibling;
        printDropdown.style.height = "";
        printContainer.current.appendChild(printDropdown);
        printDropdown
          .querySelectorAll(".routeYou_downloadLink")
          .forEach((item) =>
            item.addEventListener("click", (e) => {
              e.target.classList.toggle("routeYou_downloadLink-active");
              e.target.parentNode.nextElementSibling?.classList.toggle(
                "routeYou_downloadLink--open"
              );
            })
          );
      }
      if (title.innerText === "Downloaden") {
        const downloadDropdown = title.nextElementSibling;
        downloadDropdown.style.height = "";
        downloadContainer.current.appendChild(downloadDropdown);
        downloadDropdown
          .querySelectorAll(".routeYou_downloadLink")
          .forEach((item) =>
            item.addEventListener("click", (e) => {
              e.target.classList.toggle("routeYou_downloadLink-active");
              e.target.parentNode.nextElementSibling?.classList.toggle(
                "routeYou_downloadLink--open"
              );
            })
          );
        // The RouteYou plugin attaches a click listener on the
        // "Op mijn Garmin-toestel plaatsen" anchor (href="#") that redirects
        // via window.location using the current page URL, which results in a
        // broken Sport Vlaanderen path on routeyou.com for unauthenticated
        // users. Fix: clone the element (stripping the plugin's listener) and
        // set the correct routeyou.com URL so Garmin Connect works regardless
        // of login state. Opens in a new tab so the user stays on the page.
        downloadDropdown.querySelectorAll("p > a[href='#']").forEach((link) => {
          const clone = link.cloneNode(true);
          clone.href = `https://www.routeyou.com/nl-be/route/view/${routeData.id}#garmin-connect=1`;
          clone.target = "_blank";
          clone.rel = "noopener noreferrer";
          link.replaceWith(clone);
        });
      }
    });
    // remove the download section from the tabPlane
    downloadSection.parentNode.remove();
  };

  const togglePrint = (e) => {
    e.preventDefault();
    setIsPrintOpen(!isPrintOpen);
  };

  const toggleDownload = (e) => {
    e.preventDefault();
    setIsDownloadOpen(!isDownloadOpen);
  };

  const clickOutsidePrint = useRef(null);

  const handleClickOutsidePrint = useCallback(() => {
    setIsPrintOpen(false);
  }, []);

  useClickOutside(clickOutsidePrint, handleClickOutsidePrint);

  const clickOutsideDownload = useRef(null);

  const handleClickOutsideDownload = useCallback(() => {
    setIsDownloadOpen(false);
  }, []);

  useClickOutside(clickOutsideDownload, handleClickOutsideDownload);

  function removeEmptyParagraphsAfterLastUl(data) {

    if (!Types.KajakRoutes.includes(data.type.id)) {
      return data.description.nl || "";
    }

    const htmlString = data.description.nl || "";
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;

    const uls = tempDiv.getElementsByTagName("ul");

    if (uls.length === 0) {
      return htmlString;
    }

    const lastUl = uls[uls.length - 1];

    let currentNode = lastUl.nextSibling;
    const nodesToRemove = [];

    while (currentNode) {
      if (
        currentNode.nodeType === Node.ELEMENT_NODE &&
        currentNode.tagName === "P" &&
        currentNode.innerHTML.trim() === "&nbsp;"
      ) {
        nodesToRemove.push(currentNode);
      }
      currentNode = currentNode.nextSibling;
    }

    nodesToRemove.forEach((node) => node.remove());

    return tempDiv.innerHTML;
  }

  return (
    <div className="c-routedetail__container js-routeDetail">
      <div className="c-routedetail-return__container">
        <i
          onClick={navigateBack}
          className="fal fa-arrow-left c-routedetail-return__arrow"
        ></i>
        <a onClick={navigateBack} className="c-routedetail-return">
          Terug
        </a>
      </div>

            <div>
                <h1 className="c-routedetail-title">{title}</h1>
            </div>

            <div className="u-align u-gap-1rem">
                <img
                    className="c-routedetail-description__icon"
                    src={routeDetails ? convertTypeToIcon(routeDetails.type.id) : ""}
                />
                <h2 className="c-routedetail-subtitle u-primary-color">{loop}</h2>
            </div>

      <div className="c-routedetail-topbar">
        <div className="c-routedetail-configuration__container">
          {routeDetails?.length && (
            <div>
              <p className="c-routedetail-configuration__text">Afstand</p>

              <strong>{(routeDetails.length / 1000).toFixed(2) + "km"}</strong>
            </div>
          )}

          {(checkRouteTypeHoogteMeters() && routeDetails?.ascent?.total) ||
          (checkRouteTypeHoogteMeters() && routeDetails?.descent?.total) ? (
            <div>
              <p className="c-routedetail-configuration__text">Hoogtemeters</p>

              {routeDetails?.ascent?.total ? (
                <strong>{routeDetails?.ascent?.total + "m"}</strong>
              ) : (
                <strong>{routeDetails?.descent?.total + "m"}</strong>
              )}
            </div>
          ) : (
            <></>
          )}

          {routeDetails?.statistics?.roadCondition?.unpaved &&
            routeDetails?.type?.id != 19 && (
              <div>
                <p className="c-routedetail-configuration__text">Onverhard</p>

                <strong>
                  {(
                    (routeDetails.statistics.roadCondition.unpaved.good +
                      routeDetails.statistics.roadCondition.unpaved.bad) *
                    100
                  ).toFixed(2) + "%"}
                </strong>
              </div>
            )}

          {(routeData.difficultyAuthor != null || routeData.difficulty != null) && (
            <div>
              <p className="c-routedetail-configuration__text__difficulty">
                {dictionary.difficulty}
              </p>
              <DifficultyPill
                difficultyScore={routeData.difficultyAuthor ?? routeData.difficulty}
                dictionary={dictionary}
              />
            </div>
          )}

          {routeDetails?.type?.id == 7 &&
          routeDetails?.statistics?.singletrack?.score ? (
            <div>
              <p className="c-routedetail-configuration__text">Single tracks</p>

              <strong>
                {Math.round(routeDetails.statistics.singletrack.score * 100) +
                  "%"}
              </strong>
            </div>
          ) : (
            <></>
          )}
          {routeScore && (
            <div>
              <p className="c-routedetail-configuration__text">
                Beoordeling route
              </p>
              <strong className="c-routedetail-configuration__score">
                <SportRouteStar filled={true} />
                {routeScore}
              </strong>
            </div>
          )}
        </div>
        <div className="c-routedetail-actions">
          <div className="c-routedetail-action" ref={clickOutsideDownload}>
            <Button
              buttonType="secondary"
              isIcon={true}
              icon={<i className="fa fa-download"></i>}
              title={dictionary.downloadIconAlt}
              size="small"
              href="#"
              onClick={toggleDownload}
            />
            <div
              className={`c-routedetail__print ${
                isDownloadOpen ? "c-routedetail__print--open" : ""
              }`}
              ref={downloadContainer}
            ></div>
          </div>
          <div className="c-routedetail-action" ref={clickOutsidePrint}>
            <Button
              buttonType="secondary"
              isIcon={true}
              icon={<i className="fa fa-print"></i>}
              title={dictionary.printIconAlt}
              size="small"
              onClick={togglePrint}
              href="#"
            />
            <div
              className={`c-routedetail__print ${
                isPrintOpen ? "c-routedetail__print--open" : ""
              }`}
              ref={printContainer}
            ></div>
          </div>
          {isOffroadBikeRoute && (
            <div className="c-routedetail-action">
              <Button
                buttonType="secondary"
                isIcon={true}
                icon={<i className="fa fa-comment"></i>}
                title={dictionary.commentsIconAlt}
                size="small"
                onClick={scrollToComments}
                href="#"
              />
            </div>
          )}
          <div className="c-routedetail-action">
            <Button
              buttonType="secondary"
              isIcon={true}
              icon={<i className="fa fa-megaphone"></i>}
              title={dictionary.meldpuntIconAlt}
              size="small"
              href={meldpuntUrl}
            />
          </div>
        </div>
      </div>
      {isOffroadBikeRoute && (
        <div id="detailLeafletMap" className="c-routedetail-leaflet-map"></div>
      )}
      <div id="routeYouRouteViewerMap" className={routeViewerMapClassName}></div>

      <div className="u-split-screen mb-40">
        <div className="u-split-screen__half">
          <h3 className="c-routedetail-description-title">
            {dictionary.routeOmschrijving}
          </h3>

          <div>
            {routeDetails && (
              <div
                dangerouslySetInnerHTML={{
                  __html: description || routeDetails.description.nl,
                }}
              ></div>
            )}
          </div>

          {routeDetails && routeDetails?.generatedInfo?.nl && (
            <div
              dangerouslySetInnerHTML={{
                __html: routeDetails.generatedInfo.nl,
              }}
            ></div>
          )}

          <div className="c-routedetail-startplaces__container">
            <p className="c-routedetail-startplace-title">
              <strong>{dictionary.startplaatsen}</strong>
            </p>

            {startPlaces.length > 0 && (
              <div>
                {startPlaces.map((startplace, index) => (
                  <div
                    className="c-routedetail-startplace__container"
                    key={index}
                  >
                    <i className="fa-solid fa-flag-checkered"></i>
                    {startplace?.centroid?.wkt ? (
                      <a
                        className="c-routedetail-startplace-text"
                        href={`https://www.google.com/maps/place/${extractCoordinates(
                          startplace.centroid.wkt
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {startplace.address.street} {startplace.address.number}{" "}
                        {startplace.address.postalCode}{" "}
                        {startplace.address.city}
                      </a>
                    ) : startplace?.address ? (
                      <a
                        className="c-routedetail-startplace-text"
                        href={`https://www.google.com/maps/place/${startplace.address.street} ${startplace.address.number} ${startplace.address.postalCode} ${startplace.address.city}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {startplace.address.street} {startplace.address.number}{" "}
                        {startplace.address.postalCode}{" "}
                        {startplace.address.city}
                      </a>
                    ) : (
                      <a
                        className="c-routedetail-startplace-text"
                        href={`https://www.google.com/maps/place/${
                          startplace.name.nl.split("Startplaats")[1]
                        }`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {startplace.name.nl}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {routeDetails ? (
            media.length > 0 ? (
              <div className="c-routedetail-description-dropdown__container">
                <div id="routeYouRouteViewerTabPane" ref={tabPlane}></div>
              </div>
            ) : (
              <></>
            )
          ) : (
            <></>
          )}
        </div>

        <div className="u-split-screen__half">
          {routeDetails ? (
            media.length > 0 ? (
              <>
                <div
                  className={`c-routedetail-description-grid ${
                    showAllImages ? "" : "u-limit-grid"
                  }`}
                >
                  {media.map((item, index) => {
                    switch (item.type) {
                      case "youtube":
                        return (
                          <a href={item.url} className="c-routedetail-yt">
                            <img
                              onLoad={(e) => checkThumbnail(e)}
                              src={url}
                              alt={`Image ${index}`}
                              key={index}
                              className="c-routedetail-description-grid__image"
                            />
                            <div className="play-button">
                              <div className="play-button__icon"></div>
                            </div>
                          </a>
                        );
                      case "image":
                        return (
                          <img
                            src={item.image}
                            alt={`Image ${index}`}
                            key={index}
                            className="c-routedetail-description-grid__image"
                            onClick={() => openOverlay(index)}
                          />
                        );
                      case "pdf":
                        return (
                          <object
                            key={index}
                            data={item.url}
                            type="application/pdf"
                            width="100%"
                            height="500px"
                          >
                            <p>
                              It appears you don't have a PDF plugin for this
                              browser. No biggie... you can{" "}
                              <a href={item.url}>
                                click here to download the PDF file.
                              </a>
                            </p>
                          </object>
                        );
                      default:
                        return null;
                    }
                  })}
                </div>

                {media.length > 9 ? (
                  <div className="u-margin-top-1x u-align">
                    <i className="fal fa-arrow-right u-primary-color"></i>
                    <button
                      className="c-routedetail-description-grid__button"
                      onClick={() => {
                        setShowAllImages(!showAllImages);
                      }}
                    >
                      {showAllImages
                        ? dictionary.verbergFotos
                        : dictionary.alleFotos}
                    </button>
                  </div>
                ) : (
                  <></>
                )}
              </>
            ) : (
              <>
                <div className="c-routedetail-description-dropdown__container">
                  <div id="routeYouRouteViewerTabPane"></div>
                </div>
              </>
            )
          ) : (
            <></>
          )}
        </div>
      </div>

      <div
        className="c-routedetail-carousel__container"
        style={{ display: "none" }}
      >
        <i class="fa fa-close overlay-close" onClick={closeOverlay}></i>
        <Carousel data={media} index={clickedImageIndex} />
      </div>
      <div className="overlay" style={{ display: "none" }}></div>

      <div
        id="routeYouRouteViewerTitle"
        className="c-routedetail-viewerTitle"
      ></div>

      {isOffroadBikeRoute && (
        <div
          id="routeYouComments"
          className="c-routedetail-comments"
          ref={commentsArea}
        ></div>
      )}
    </div>
  );
}
